<template>
  <div id="app-2019-11-tuloryhmat">
    <div class="intro">
      <div class="content-box">
        <h1 class="heading heading--h1">Paljonko sinä tienaat? Vertaa itseäsi muihin veronmaksajiin</h1>
        <p><strong>Uusimmat verotiedot julkaistiin maanantaina, jolloin valokeilassa olivat Suomen suurituloisimmat. He ovat kuitenkin vain murto-osa kaikista 5,1 miljoonasta veronmaksajasta.</strong></p>
        <p>Tiedätkö miten omat tulosi sijoittuvat, jos verrokkina käytetään muita veronmaksajia?</p>
        <label for="income-input">Paljonko ansaitset vuodessa? Aloita kirjoittamalla bruttotulosi:</label>
        <input id="income-input" v-model.number="income" class="yddviz-input" type="number">
        <p>Bruttotuloilla tarkoitetaan tuloja ennen veroja ja vähennyksiä. Tuloja ovat esimerkiksi palkka (sisältäen lomarahat), eläke, tuet sekä pääomatulot.</p>
      </div>
    </div>
    <div class="fyrfalt-scroll_wrapper" v-if="incomeGroup !== null">
      <div class="fyrfalt-background">
        <div class="svg-container">
          <transition-group name="fade">
            <svg :viewBox="viewBox" class="svg-content" id="inkomstgrupper-svg" v-show="mounted && !showTaxes" key="mainSVG">
              <text class="legend" v-if="currentView !== 'all'" x="480" y="200">{{ inkomstgrupper[incomeGroup].text }}</text>
            </svg>
            <svg :viewBox="viewBox" class="taxes-svg" v-show="showTaxes" key="taxSVG">
              <transition-group name="fade" tag="g">
                <rect
                  v-if="showAllTaxes"
                  :width="tax2area(allTaxes)"
                  :height="tax2area(allTaxes)"
                  fill="#c8c3be"
                  key="allTaxes"
                />
                <text fill="#000" x="10" :y="tax2area(allTaxes) + 20" v-if="showAllTaxes" key="allTaxesLabel">
                  {{ allTaxes.toLocaleString('sv-FI') }} €
                </text>
                <rect
                  v-if="!showRichestTaxes"
                  :width="tax2area(inkomstgrupper[incomeGroup].taxes)"
                  :height="tax2area(inkomstgrupper[incomeGroup].taxes)"
                  fill="#ff4d5b"
                  key="yourTaxes"
                />
                <text fill="#000" x="10" :y="tax2area(inkomstgrupper[incomeGroup].taxes) + 20" key="yourTaxesLabel" v-if="!showRichestTaxes">
                  {{ inkomstgrupper[incomeGroup].taxes.toLocaleString('sv-FI') }} €
                </text>
                <rect
                  v-if="showRichestTaxes"
                  :width="tax2area(inkomstgrupper[11].taxes)"
                  :height="tax2area(inkomstgrupper[11].taxes)"
                  fill="#ff4d5b"
                  key="richestTaxes"
                />
                <text fill="#000" x="10" :y="tax2area(inkomstgrupper[11].taxes) + 20" key="richestTaxesLabel" v-if="showRichestTaxes">
                  {{ inkomstgrupper[11].taxes.toLocaleString('sv-FI') }} €
                </text>
              </transition-group>
            </svg>
          </transition-group>
        </div>
      </div>
      <div class="fyrfalt-content">
        <div class="content-box first" data-trigger="intro" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>
            Nämä tuhat punaista palloa edustavat kaikkia veronmaksajia. Yksi punainen pallo vastaa promillea veronmaksajista.
          </p>
          <p>Näin <strong>vuositulot</strong> jakautuivat Suomessa vuonna 2017. Vieritä alaspäin nähdäksesi lisää.</p>
        </div>
        <div class="content-box" data-trigger="showYour" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>
            Kuulut ryhmään, joka ansaitsee {{ inkomstgrupper[incomeGroup].text }} euroa vuodessa. Teitä edustaa {{ inkomstgrupper[incomeGroup].total_dots }} punaista <span v-if="incomeGroup < 11">pistettä</span><span v-else>piste</span>, eli olette {{ (inkomstgrupper[incomeGroup].total_dots / 10).toLocaleString('sv-FI') }} prosenttia veronmaksajista.
          </p>
        </div>
        <div class="content-box" data-trigger="showLess" v-waypoint="{ active: true, callback: onWaypoint }" v-if="lowerIncome">
          <p>
            Veronmaksajista {{ lowerIncome }} prosentilla oli pienemmät tulot kuin sinun ryhmälläsi.
          </p>
        </div>
        <div class="content-box" data-trigger="showMore" v-waypoint="{ active: true, callback: onWaypoint }" v-if="higherIncome">
          <p>Puolestaan {{ higherIncome }} prosenttia veronmaksajista ansaitsi enemmän kuin ryhmäsi.</p>
        </div>
        <div class="content-box" data-trigger="showHighest" v-waypoint="{ active: true, callback: onWaypoint }" v-if="incomeGroup < 11">
          <p>Vain noin promille veronmaksajista kuuluu suurituloisimpien ryhmään, joka ansaitsee yli 300&nbsp;000 euroa vuodessa.</p>
        </div>
        <div class="content-box" data-trigger="hideOther" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>Tarkastellaan seuraavaksi tarkemmin sinun tuloryhmääsi.</p>
        </div>
        <div class="content-box" data-trigger="showGenders" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>
            {{ inkomstgrupper[incomeGroup].men_share }} prosenttia ryhmästäsi on <strong class="men">miehiä</strong>.
            {{ inkomstgrupper[incomeGroup].women_share }} prosenttia  on <strong class="women">naisia</strong>.
          </p>
        </div>
        <div class="content-box" data-trigger="showAges" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>
            Ryhmänne ikäjakauma on tällainen. Useimmat teistä kuuluvat {{ inkomstgrupper[incomeGroup].age }}-vuotiaiden ryhmään.
          </p>
        </div>
        <div class="content-box" data-trigger="showTaxes" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>
            Tuloryhmäsi maksoi vuonna 2017 keskimäärin veroja {{ inkomstgrupper[incomeGroup].taxes_per_person.toLocaleString('sv-FI') }} euroa.
            Yhteensä se tekee {{ inkomstgrupper[incomeGroup].taxes.toLocaleString('sv-FI') }} euroa!
          </p>
        </div>
        <div class="content-box" data-trigger="showAllTaxes" v-waypoint="{ active: true, callback: onWaypoint }">
          <p>

            Se on {{ percentTaxes.toLocaleString('sv-FI') }} prosenttia kaikista yksityishenkilöiden maksamista tuloveroista.
          </p>
        </div>
        <div class="content-box" data-trigger="showRichestTaxes" v-waypoint="{ active: true, callback: onWaypoint }" v-if="incomeGroup < 11">
          <p>
            Suurituloisimpien veronmaksajien promille maksoi kuusi prosenttia kaikista yksityishenkilöiden tuloveroista.
          </p>
        </div>
      </div>
    </div>
    <div class="end" v-if="incomeGroup !== null">
      <div>
        <div class="content-box">
          <h2>Kiitos veroista!</h2>
          <p class="credits">Kaikkien veronmaksajien verotiedot vuoden 2018 osalta julkaistaan maaliskuussa 2020. Maanantaina 4. marraskuuta 2019 julkistettiin Suomen 10 000 suurituloisimman veronmaksajan tiedot.</p>
          <p class="credits">Jutun on julkaissut alun perin <a href="https://svenska.yle.fi/artikel/2019/11/06/vad-tjanar-du-jamfor-dig-med-resten-av-finlands-skattebetalare">Svenska Yle</a> 6.11.2019</p>
          <p class="credits">
            Tekijät: Linus Lång, Petter West, Malin Ekholm, Juha Rissanen
          </p>
          <p class="credits">Lähde: Verohallinto.</p>
          <p class="credits">Julkaistu 8.11.2019.</p>
          <p class="credtis"><em>Juttua korjattu 11.11.2019: Kuukausitulojen sijaan kysytään vuosituloja.</em></p>
          <p class="credits">Lue lisää: <a href="https://yle.fi/uutiset/3-11052552">Tätä selvemmin sitä ei voi piirtää: Nuoret naiset ovat kaukana tulokärjestä</a></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import inkomstgrupper from './inkomstgrupper.json'
import aldersgrupper from './aldersgrupper.json'

const alderLabels = ["0–17 v", "18–24 v", "25–34 v", "35–44 v", "45–54 v", "55–64 v", "65–74 v", "75–84 v", "+85 v"];
const duration = 2000;
const delayMax = 1000;

let svg, taxScale;

const colors = {
  selected: '#ff4d5b',
  deselected: '#c8c3be',
  woman: '#60C8AC',
  man: '#6F73E1',
}

d3.formatDefaultLocale({
  'decimal': ',',
  'thousands': ' ',
});

export default {
  name: 'app',
  components: {
  },
  data() {
    return {
      mounted: false,
      inkomstgrupper,
      aldersgrupper,
      income: null,
      dim: {
        width: 500,
        height: 600,
      },
      time: 0,
      dataSets: [],
      graphLayer: null,
      axisLayer: null,
      xScale: null,
      yScale: null,
      yAxisObj: null,
      radius: 3,
      mar: 0.6,
      barWidth: null,
      colorScale: null,
      currentView: 'all',
      groups: [167,89,109,101,92,175,177,52,21,11,5,1],
      allTaxes: 29946810686,
      showTaxes: false,
      showAllTaxes: false,
      showRichestTaxes: false,
    }
  },
  mounted() {
    taxScale = d3.scaleSqrt()
      .domain([0, this.allTaxes])
      .range([0, this.dim.width])
  },
  computed: {
    percentTaxes() {
      return d3.format(".1f")(this.inkomstgrupper[this.incomeGroup].taxes / this.allTaxes * 100);
    },
    viewBox() {
      return `0 0 ${this.dim.width} ${this.dim.height}`;
    },
    yLocalScale() {
      return d3.scaleBand().range([0,this.yScale.bandwidth()]).domain(d3.range(this.barWidth));
    },
    incomeGroup() {
      const income = this.income;
      if (income) {
        if (income < 5000) {
          return 0;
        }
        else if (income < 10000) {
          return 1;
        }
        else if (income < 15000) {
          return 2;
        }
        else if (income < 20000) {
          return 3;
        }
        else if (income < 25000) {
          return 4;
        }
        else if (income < 35000) {
          return 5;
        }
        else if (income < 55000) {
          return 6;
        }
        else if (income < 75000) {
          return 7;
        }
        else if (income < 100000) {
          return 8;
        }
        else if (income < 150000) {
          return 9;
        }
        else if (income < 300000) {
          return 10;
        }
        else return 11;
      }
      else return null;
    },
    lowerIncome() {
      if (this.incomeGroup) {
        return (d3.sum(this.groups.slice(0, this.incomeGroup)) / 10).toLocaleString('sv-FI');
      }
      return null;
    },
    higherIncome() {
      if (this.incomeGroup === 11) {
        return null;
      }
      return (d3.sum(this.groups.slice(this.incomeGroup + 1, 12)) / 10).toLocaleString('sv-FI');
    },
  },
  watch: {
    incomeGroup(newValue) {
      if (this.mounted === false) {
        this.$nextTick(() => {
          svg = d3.select('#inkomstgrupper-svg');
          this.initD3();
          this.mounted = true;
        })
      }
      if (newValue === null) {
        this.mounted = false;
      }
    },
    currentView() {
      this.trans();
    },
  },
  methods: {
    tax2area(tax) {
      return taxScale(tax);
    },
    onWaypoint({ going, el }) {
      if (going === this.$waypointMap.GOING_IN) {
        switch (el.dataset.trigger) {
          case 'intro':
            this.reset();
            break;
          case 'showYour':
            this.colorDots('same');
            this.currentView = 'all';
            break;
          case 'showLess':
            this.colorDots('less');
            this.currentView = 'all';
            break;
          case 'showMore':
            this.colorDots('more');
            this.currentView = 'all';
            break;
          case 'showHighest':
            this.colorDots('highest');
            this.currentView = 'all';
            break;
          case 'hideOther':
            this.colorDots('same');
            this.currentView = 'all';
            break;
          case 'showGenders':
            this.colorDots('genders');
            this.currentView = 'all';
            break;
          case 'showAges':
            this.colorDots('same');
            this.currentView = 'ages';
            this.showTaxes = false;
            break;
          case 'showTaxes':
            this.showTaxes = true;
            this.showAllTaxes = false;
            break;
          case 'showAllTaxes':
            this.showAllTaxes = true;
            this.showRichestTaxes = false;
            break;
          case 'showRichestTaxes':
            this.showRichestTaxes = true;
            break;
        }
      }
    },
    reset() {
      this.currentView = 'all';
      this.colorDots(null);
    },
    trans() {
      const yAxis = d3.axisLeft(this.yScale);
      const max = d3.max(this.groups);
      const nrow = Math.ceil(this.dim.graphWidth / (2 * (this.radius + this.mar)));
      this.barWidth = Math.ceil(max/nrow);
      this.xScale.domain(d3.range(nrow));

      if (this.currentView === 'all') {
        this.yScale.domain(this.groups.map((d,i) => {return i;}));
        yAxis.tickFormat((d) => {return this.inkomstgrupper[d].name});
      }
      else {
        this.yScale.domain(alderLabels.map((d,i) => {return i;}));
        yAxis.tickFormat((d) => {return alderLabels[d]});
      }
      this.yAxisObj.call(yAxis);

      this.graphLayer.selectAll('.dot')
        .transition()
        .duration(duration)
        .filter((d) => {
          return d['all'].group != this.incomeGroup;
        })
        .style('opacity', this.currentView === 'all' ? 0.8 : 0.0);

      this.graphLayer.selectAll('.dot')
        .filter((d) => {
          return d['all'].group == this.incomeGroup;
        })
        .transition()
        .duration(duration)
        .delay(() => {return Math.random()*delayMax;})
        .attr('cy', (d) => {return ((d[this.currentView].group != null) ? (this.yScale(d[this.currentView].group) + this.yLocalScale(d[this.currentView].idx%this.barWidth) + this.radius + this.mar) : (this.dim.graphHeight/2));})
        .attr('cx', (d) => {return ((d[this.currentView].group != null) ? (this.xScale(Math.floor((d[this.currentView].idx+0.1)/this.barWidth)) - this.radius - this.mar) : 0);})
        .style('opacity', (d) => {return (d[this.currentView].group != null) ? 0.8 : 0.0;});
    },
    colorDots(showGroup) {
      this.graphLayer.selectAll('.dot')
        .style('fill', (d) => {
          switch (showGroup) {
            case 'more':
              if (d['all'].group > this.incomeGroup) {
                return colors.selected;
              }
              else return colors.deselected;
            case 'highest':
              if (d['all'].group === 11) {
                return colors.selected;
              }
              else return colors.deselected;
            case 'less':
              if (d['all'].group < this.incomeGroup) {
                return colors.selected;
              }
              else return colors.deselected;
            case 'same':
              if (d['all'].group === this.incomeGroup) {
                return colors.selected;
              }
              else return colors.deselected;
            case 'genders':
              if (d['all'].group !== this.incomeGroup) {
                return colors.deselected;
              }
              else if (d['all'].idx < this.inkomstgrupper[d['all'].group].men_dots) {
                return colors.man;
              }
              else return colors.woman;
            default:
              return colors.selected;
          }
        });
    },
    initD3() {
      const margin = {top: 10, bottom: 10, left: 100, right: 10};
      this.dim.graphWidth = this.dim.width - margin.left - margin.right;
      this.dim.graphHeight = this.dim.height - margin.top - margin.bottom;
      this.axisLayer = svg.append('g').attr('transform','translate(' + margin.left + ',' + margin.top + ')');
      this.graphLayer = svg.append('g').attr('transform','translate(' + margin.left + ',' + margin.top + ')');

      this.xScale = d3.scaleBand().range([0, this.dim.graphWidth]).paddingOuter(0);
      this.yScale = d3.scaleBand().range([0, this.dim.graphHeight]).paddingOuter(0).paddingInner(0.3);
      this.colorScale = d3.scaleOrdinal(['#00b4c8']);

      const yAxis = d3.axisLeft(this.yScale);

      this.yAxisObj = this.axisLayer.append('g')
        .attr('transform','translate(-10, 0)')
        .attr('class','axis')
        .call(yAxis);

      const max = d3.max(this.groups);
      const nrow = Math.ceil(this.dim.graphWidth / (2 * (this.radius + this.mar)));
      this.barWidth = Math.ceil(max/nrow);
      this.xScale.domain(d3.range(nrow));
      this.yScale.domain(this.groups.map((d,i) => {return i;}));
      yAxis.tickFormat((d) => {return this.inkomstgrupper[d].name});
      this.yAxisObj.call(yAxis);

      let displaydata = d3.range(d3.sum(this.groups)).map(() => { return { 'all': {}, 'ages': {}}}); // create 1000 dots
      let indexMargin = 0;
      this.groups.forEach((group, groupidx) => {
        const agegroups = Object.values(this.aldersgrupper[groupidx]);
        let ageid = 0;
        let agegroupidx = 0;
        for (let i=0; i < group; ++i) {
          displaydata[indexMargin+i]['all'] = {group: groupidx, idx: i}; // calculate which income group the dot belongs to

          while (ageid >= agegroups[agegroupidx]) {
            ++agegroupidx;
            ageid = 0;
          }

          displaydata[indexMargin+i]['ages'] = {group: agegroupidx, idx: ageid}; // calculate which age group the dot belongs to
          ++ageid;
        }

        indexMargin += group;
      });

      this.graphLayer.selectAll('.dot').data(displaydata).enter().append('circle')
        .attr('class','dot')
        .attr('r',this.radius)
        .attr('cy', (d) => {return ((d[this.currentView].group!=null) ? (this.yScale(d[this.currentView].group) + this.yLocalScale(d[this.currentView].idx%this.barWidth) + this.radius + this.mar) : (this.dim.graphHeight/2));})
        .attr('cx', (d) => {return ((d[this.currentView].group!=null) ? (this.xScale(Math.floor((d[this.currentView].idx + 0.1) / this.barWidth)) - this.radius - this.mar) : 0);})
        .style('opacity', (d) => {return (d[this.currentView].group!=null) ? 0.8 : 0.0;})
        .style('fill', colors.selected);
    },
  },
}
</script>

<style lang="scss">
#app-2019-11-tuloryhmat {
  font-family: Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  max-width: 600px;
  margin: 0 auto;

  .heading--h1 {
    font-family: 'Roboto', Arial, Helvetica, sans-serif;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.4em;
    margin-bottom: 10px;
    text-align: left;

    @media all and (min-width: 768px) {
      font-size: 32px;
    }
  }

  h2 {
    font-family: 'Roboto', Arial, Helvetica, sans-serif;
    font-size: 2em;
    line-height: 1.2em;
    margin: 0.85em auto;
  }

  h3 {
    font-family: 'Roboto', Arial, Helvetica, sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 1.5em;
    margin: 0.85em auto;
  }

  a {
    color: #0d81b3;
    text-decoration: none;
  }

  p {
    font-family: 'Roboto', Arial, Arial, Helvetica, sans-serif;
    font-size: 17px;
    margin: auto auto 0.85em auto;
    line-height: 1.45em;

    strong {
      font-weight: 700;
    }
  }

  label {
    display: block;
    font-family: 'Roboto', Arial, Arial, Helvetica, sans-serif;
    font-size: 17px;
    line-height: 1.45em;
    margin: 0 0 0.85em 0;
  }

  .women {
    color: #60C8AC;
  }

  .men {
    color: #6F73E1;
  }

  svg {
    display: block;
    width: 100%;
    height: auto;

    .dot {
      transition: fill .2s;
    }

    .legend {
      font-family: 'Roboto', Arial, Helvetica, sans-serif;
      font-size: 22px;
      font-weight: bold;
      fill: #ff4d5b;
      text-anchor: end;
    }
  }

  .svg-container {
    position: relative;
  }

  .fyrfalt-background {
    position: sticky;
    top: 50px;
    overflow: hidden;
    z-index: -1;
  }

  .fyrfalt-content {
    max-width: 700px;
    margin: 0 auto;
    padding-bottom: 90vh;
    margin-top: -50vh;

    .content-box {
      background-color: rgba(255, 255, 255, 0.9);
      margin-top: 95vh;
      padding: 20px;
      line-height: 1.4;
      transform: translate3d(0, 0, 0);

      &.first {
        margin-top: 0;
      }

      h1, h2, h3 {
        margin-top: 0;
      }

      > p:last-child {
        margin-bottom: 0;
      }

      > p:first-child {
        margin-top: 0;
      }
    }

    .factbox {
      margin-left: -20px;
      margin-right: -20px;
      background: #fff;
      padding: 20px;
    }

    .factbox-collapsed {
      width: 100%;
    }
  }

  .intro {
    width: 100%;
    position: relative;

    .content-box {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.5;
    }
  }

  .axis {
    text {
      font-size: 18px;
      font-family: 'Roboto', Arial, Helvetica, sans-serif;

      @media all and (min-width: 600px) {
        font-size: 14px;
      }
    }

    path.domain {
      fill: none;
      stroke: #000;
      shape-rendering: crispEdges;
    }

    line {
      fill: none;
      stroke: #000;
      shape-rendering: crispEdges;
    }
  }

  .end {
    line-height: 1.4;
    padding-top: 10px;

    .content-box {
      max-width: 800px;
      margin: 0 auto;
      padding: 15px;
    }
  }
}
.yddviz-input {
  display: block;
  border: 1px solid #000;
  border-radius: 6px;
  padding: 6px;
  width: 100%;
  margin: 0.85em auto 0.85em 0;
  max-width: 400px;
  font-size: 16px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border: 2px solid;
  }
}

.taxes-svg {
  text {
    font-family: 'Roboto', Arial, Helvetica, sans-serif;
    font-size: 20px;

    @media all and (min-width: 600px) {
      font-size: 14px;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;

  &.taxes-svg {
    position: absolute;
    top: 0;
  }
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.ydd-article {
  padding-top: 0;
}

.messages.messages--warning, .l-region--navigation, .l-region--branding, .node__status {
  display: none;
}

.node-unpublished {
  background-color: transparent;
}

ul.tabs--primary {
  position: relative;
  z-index: 2;
}

.ydd-feature-article-related-container {
  background: #fff;
  z-index: 2;
  position: relative;
}

.js-yle-article-share-buttons, .ydd-footer {
  z-index: 2;
}

.span-12.comments-and-form-wrapper {
  background: #fff;
}
</style>
